import { c as _c } from "react/compiler-runtime";
import type { ItemPriceResultDto, ProductPublicDto } from "@sticker-giant/sg-frontend-common";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { buildAllQuantitiesPricingRequest, buildCustomQuantityPricingRequest } from "@/lib/product/pricing";
import { getQuantityOptionsForMaterial } from "@/lib/product/quantities";
import { usePdpStore } from "@/stores/usePdpStore";
import { useServicesStore } from "@/stores/useServicesStore";
import { PackagingMode, resolvePackagingMode } from "@/utils/resolvePackagingMode";
import { useCustomerSkus } from "./useCustomer";
function useOverrides(product) {
  const $ = _c(8);
  const material = usePdpStore(_temp) ?? product?.defaultMaterial;
  const finish = usePdpStore(_temp2);
  const width = usePdpStore(_temp3);
  const height = usePdpStore(_temp4);
  const rollCount = usePdpStore(_temp5);
  const rollQty = usePdpStore(_temp6);
  const rollDriver = usePdpStore(_temp7);
  const orientation = usePdpStore(_temp8) ?? product?.defaultOrientation;
  const isRollWithoutPreference = resolvePackagingMode(finish ?? product?.defaultFinishing) === PackagingMode.Roll && rollDriver === null;
  const t0 = isRollWithoutPreference ? null : rollCount;
  const t1 = isRollWithoutPreference ? null : rollQty;
  let t2;
  if ($[0] !== finish || $[1] !== height || $[2] !== material || $[3] !== orientation || $[4] !== t0 || $[5] !== t1 || $[6] !== width) {
    t2 = {
      material,
      finish,
      width,
      height,
      orientation,
      rollCount: t0,
      rollQty: t1
    };
    $[0] = finish;
    $[1] = height;
    $[2] = material;
    $[3] = orientation;
    $[4] = t0;
    $[5] = t1;
    $[6] = width;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function _temp8(state_6) {
  return state_6.orientation;
}
function _temp7(state_5) {
  return state_5.rollDriver;
}
function _temp6(state_4) {
  return state_4.rollQty;
}
function _temp5(state_3) {
  return state_3.rollCount;
}
function _temp4(state_2) {
  return state_2.height;
}
function _temp3(state_1) {
  return state_1.width;
}
function _temp2(state_0) {
  return state_0.finish;
}
function _temp(state) {
  return state.material;
}
export function useQuantityPricing(product?: ProductPublicDto) {
  const {
    pricingClientPublic
  } = useServicesStore.getState();
  const {
    customerSkus,
    isResolved: isCustomerResolved
  } = useCustomerSkus();
  const overrides = useOverrides(product);
  const isHydrated = usePdpStore(state => state.isHydrated);
  const quantities = product ? getQuantityOptionsForMaterial(product, overrides.material) : [];
  const request = product && quantities.length > 0 ? buildAllQuantitiesPricingRequest({
    product,
    quantities,
    overrides,
    customerSkus
  }) : undefined;
  return useQuery<ItemPriceResultDto | undefined>({
    queryKey: ["quantity-pricing", product?.sku, overrides.material, overrides.finish, overrides.width, overrides.height, overrides.orientation, overrides.rollCount, overrides.rollQty, ...customerSkus],
    // Gated on hydration so a cart-item edit never issues a first request describing the product's defaults,
    // and on the customer lookup so an exempt customer is never shown generic pricing first
    enabled: isHydrated && isCustomerResolved && Boolean(pricingClientPublic) && Boolean(request),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!pricingClientPublic || !request) {
        throw new Error("Pricing client is not available or pricing request is missing.");
      }
      return await pricingClientPublic.getPricingForItem(request);
    }
  });
}
export function useCustomQuantityPricing(product?: ProductPublicDto) {
  const {
    pricingClientPublic
  } = useServicesStore.getState();
  const {
    customerSkus,
    isResolved: isCustomerResolved
  } = useCustomerSkus();
  const overrides = useOverrides(product);
  const customQuantity = usePdpStore(state => state.quantity);
  const isHydrated = usePdpStore(state_0 => state_0.isHydrated);
  const presetQuantities = product ? getQuantityOptionsForMaterial(product, overrides.material) : [];
  const isCustom = customQuantity !== undefined && !presetQuantities.includes(customQuantity);
  const request = product && isCustom ? buildCustomQuantityPricingRequest({
    product,
    customQuantity,
    overrides,
    customerSkus
  }) : undefined;
  return useQuery<ItemPriceResultDto | undefined>({
    queryKey: ["custom-quantity-pricing", product?.sku, customQuantity, overrides.material, overrides.finish, overrides.width, overrides.height, overrides.orientation, overrides.rollCount, overrides.rollQty, ...customerSkus],
    enabled: isHydrated && isCustomerResolved && Boolean(pricingClientPublic) && Boolean(request),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!pricingClientPublic || !request) {
        throw new Error("Pricing client is not available or pricing request is missing.");
      }
      return await pricingClientPublic.getPricingForItem(request);
    }
  });
}